import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, Fragment} from '@api'
import {Dialog, DialogContent, Paper} from '@material-ui/core'
import {ChevronLeftSharp, ChevronRight, ChevronRightSharp} from '@material-ui/icons'
import {useVipLocalStore, utils, VipLocalStore} from '@modules'
import {autorun, reaction} from 'mobx'
import {Header, Footer} from './'
import {useHotkeys} from 'react-hotkeys-hook'
import {CurrentPageView} from './CurrentPageView'
import {PageThumbnailsNavigator} from './PageThumbnailsNavigator'
import {DEFAULT_CATALOG_USER_SETTINGS, IPresentationCatalogPage, site, StoresInstance} from '@stores'
import * as css from './PresentationModeContent.css'
import {PrimaryIconButton} from 'centric-ui-components'

/// region Store

const PINCH_ZOOM_THROTTLE_MS = 20

export class PresentationModeContentStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
    stores.catalog.componentStores.presentation = this

    this.emptyPresentationPage = {
      size: {width: 1600, height: 900},
      content: stores.strings.intentionallyBlank
    }
  }

  hideFooterDelayed: number

  @action private onHideFooter_delayed = () => {
    this.hideFooterDelayed = window.setTimeout(() => this.hideToolbars = true, 2500)
  }

  @action onShowFooter = () => {
    window.clearTimeout(this.hideFooterDelayed)
    this.hideToolbars = false
  }

  private preventPinchZoom = (e) => this.settings.show && e.ctrlKey && e.preventDefault()

  registerEffects = () => {
    const {onHideFooter_delayed, onShowFooter} = this,
      _toDispose: Array<IDisposer> = [
        reaction(() => this.pageNumber, () => {
          this.settings.zoom = this.defaultZoom
          this.resetPan()
        }),
        autorun(() => {
          site.isWindowFocused ? onShowFooter() : onHideFooter_delayed()
        }),
        autorun(() => {
          if (this.settings.show && _.isEmpty(this.pages)) {
            this.generateReport()
          }
        })
      ]

    window.addEventListener('wheel', this.preventPinchZoom, {passive: false})

    return () => {
      window.removeEventListener('wheel', this.preventPinchZoom)
      _toDispose.forEach(d => d())
    }
  }

  //toggleNavigator, zoomOut, zoomIn, toggleFullscreen,pageNumber, pages

  get settings() {
    return this.catalog.presentation.settings
  }

  @action toggleShow = (e: React.MouseEvent) => {
    //if (e.currentTarget)
    this.settings.show = !this.settings.show
  }

  get pageNumber() {
    return this.settings.pageNumber ?? 1
  }

  @observable emptyPresentationPage: IPresentationCatalogPage

  set page(page) {
    this.pageNumber = this.pages.indexOf(page) + 1
  }

  @computed get page() {
    const {pages, pageNumber} = this
    if (_.isEmpty(pages)) {
      return this.emptyPresentationPage
    }

    return pages[pageNumber - 1]
  }

  set pageNumber(page: number) {
    this.settings.pageNumber = Math.max(1, Math.min(page, this.pages.length))
  }

  @computed get hasNextPage() {
    return this.pageNumber < this.pages.length - 1
  }

  @computed get hasPreviousPage() {
    return this.pageNumber > 1
  }

  @action gotoNextPage = (e?: React.MouseEvent) => {
    this.pageNumber++
    e && utils.eatMouseEvent(e)
  }

  @action gotoPreviousPage = (e?: React.MouseEvent) => {
    this.pageNumber--
    e && utils.eatMouseEvent(e)
  }

  @action resetSize = () => {
    this.resetPan()
    this.page.size = null
  }

  @action resetPan = () => {
    this.translate = {x: 0, y: 0}
  }

  @observable translate = {x: 0, y: 0}

  get pages(): Array<IPresentationCatalogPage> { return this.settings.pages }

  @observable hasContentLoaded = false

  @observable hideToolbars = false

  @computed get footerPageXOfYText() {
    const {pages, pageNumber} = this
    return _.isEmpty(pages)
           ? ``
           : `${pageNumber.toLocaleString()} of ${pages.length.toLocaleString()}`
  }

  @action toggleNavigator = () => {
    this.settings.showNavigator = this.settings.showNavigator !== true
  }

  @action toggleScrollbars = () => {
    this.settings.showScrollbars = this.settings.showScrollbars !== true
  }

  @action updateGridColumns = (e, value: number) => {
    this.page.gridColumns = value
    this.page.autoColumns = false
  }

  @action toggleAutoGridColumns = () => {
    this.page.autoColumns = this.page.autoColumns != true
  }

  minZoom = .2

  get defaultZoom() { return DEFAULT_CATALOG_USER_SETTINGS.presentation.zoom}

  zoomMultiplier = 1.2
  maxZoom = 3
  pinchZoomDelta = .025

  @action pinchZoom_throttled = _.throttle((zoomIn: boolean) => {
    const {settings, minZoom, maxZoom, pinchZoomDelta} = this
    if (zoomIn) {
      settings.zoom = Math.max(minZoom, settings.zoom + pinchZoomDelta)
    } else {
      settings.zoom = Math.max(minZoom, settings.zoom - pinchZoomDelta)
    }
  }, PINCH_ZOOM_THROTTLE_MS)

  @computed get canZoomOut() {
    return this.settings.zoom > this.minZoom
  }

  @computed get canZoomIn() {
    return this.settings.zoom < this.maxZoom
  }

  @action zoomOut = () => {
    this.settings.zoom = Math.max(this.settings.zoom / this.zoomMultiplier, this.minZoom)
  }

  @action zoomIn = () => {
    this.settings.zoom = Math.min(this.settings.zoom * this.zoomMultiplier, this.maxZoom)
  }

  // @observable fullscreen = false

  @action toggleFullscreen = () => {
    const {settings} = this

    settings.fullScreen = !settings.fullScreen

    // this.fullscreen = !this.fullscreen
    // document.documentElement.requestFullscreen()
  }

  @action generateReport = () => {
    const {catalog: {groupBy: {hierarchyGroups}}, settings} = this

    if (!_.isEmpty(hierarchyGroups)) {
      settings.pages = hierarchyGroups[0].groupsInOrder.map(v => {
        if (v.store) {
          const {store: {fullTitle, props: {group}}} = v

          return {title: fullTitle, autoColumns: true, gridColumns: 5, info: {type: 'group', group}} as IPresentationCatalogPage
        }

        return null
      }).filter(_.identity)
    } else {

    }
  }

  @action clearReport = () => {
    const {catalog: {groupBy}, settings} = this
    settings.pages = []
  }
}

export const PresentationModeContentStoreContext = React.createContext<PresentationModeContentStore>(null)
export const usePresentationModeContentStore = () => React.useContext(PresentationModeContentStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const PresentationModeContent = observer<MyProps>(function PresentationModeContent({className, ...props}) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new PresentationModeContentStore(stores, obsProps)),
    {catalog: {presentation}, onShowFooter, site: {isShowingPopover}} = store

  useEffect(() => store.registerEffects(), [])

  useHotkeys('left', () => {
    presentation && store.gotoPreviousPage()
  }, [store, presentation])

  useHotkeys('right', () => {
    presentation && store.gotoNextPage()
  }, [store, presentation])

  if (!presentation) { return null}

  const {settings: {show, fullScreen}, togglePresentationMode} = presentation

  if (!fullScreen && !store.settings.show) return null

  const contents = <Fragment>
    <Header className={css.header}/>
    <CurrentPageView/>
    <PageThumbnailsNavigator/>
    <Footer/>
  </Fragment>

  return (
    <PresentationModeContentStoreContext.Provider value={store}>
      {fullScreen ?
       <Dialog open={show === true}
               className={classNames(css.root, className)}
               classes={{paper: css.paper}}
               onClose={togglePresentationMode}
               disableEscapeKeyDown={isShowingPopover}
               onMouseEnter={onShowFooter}
       >
         {contents}
       </Dialog> :
       <div
         data-show={show}
         data-dialog={false}
         className={classNames(css.root, className)}
         onMouseEnter={onShowFooter}
       >
         <Paper className={css.paper}>
           {contents}
         </Paper>
       </div>}
    </PresentationModeContentStoreContext.Provider>)
})

export const PageNavigationChevronButtons = observer(function PageNavigationChevronButtons() {
  const {pageNumber, hasNextPage, hasPreviousPage, gotoNextPage, gotoPreviousPage} = usePresentationModeContentStore()

  return (<Fragment>
    {hasPreviousPage && <PrimaryIconButton icon={<ChevronLeftSharp/>} wrapperClassName={css.previousPage} onClick={gotoPreviousPage}/>}
    {hasNextPage && <PrimaryIconButton icon={<ChevronRightSharp/>} wrapperClassName={css.nextPage} onClick={gotoNextPage}/>}
  </Fragment>)
})
/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/PresentationMode/PresentationModeContent.tsx